import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import CardMedia from "@material-ui/core/CardMedia";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ClearIcon from "@material-ui/icons/Clear";
import ThreeDRotation from "@material-ui/icons/ThreeDRotation";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import ReactMarkdown from "react-markdown";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";
import ImageGallery, { GalleryItem } from "react-image-gallery";
import _ from "lodash";

import { DRAWER_WIDTH, DRAWER_WIDTH_MOBILE } from "../../../constants";
import { ParsedRecord, Image } from "../../../types/airtable";
import placeholderImg from "../../../assets/placeholder-thumb.png";
import { DataLabel } from "./DataLabel";
import ThreeSixtyViewer from "./ThreeSixtyViewer";
import {
  setZoomPeak,
  hoverImage,
  hoverPeak
} from "../../../store/selection/actions";
import { RootState } from "../../../store";

import "react-image-gallery/styles/image-gallery.css";
import "./InfoPanel.scss";

interface ICloseInfo {
  (): void;
}

interface ICycleRecord {
  (direction: "previous" | "next"): void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      [theme.breakpoints.down("sm")]: {
        width: DRAWER_WIDTH_MOBILE
      },
      [theme.breakpoints.up("md")]: {
        width: DRAWER_WIDTH
      }
    }
  })
);

function InfoPanel({
  record,
  closeInfo,
  cycleRecord
}: {
  record: ParsedRecord;
  closeInfo: ICloseInfo;
  cycleRecord: ICycleRecord;
}): JSX.Element {
  const [isClosed, setClosed] = useState(false);
  const classes = useStyles();
  const dispatch = useDispatch();

  const zoomedPeak = useSelector(
    (state: RootState) => state.selection.selection.zoomedPeak
  );

  useEffect(() => {
    // Set hover peak when first rendered
    dispatch(hoverPeak(record.PEAK_ID));
  }, [record]);

  useEffect(() => {
    // Remove hover peak when zoomed peak is set (not null)
    zoomedPeak && dispatch(hoverPeak(null));
  }, [zoomedPeak]);

  const handleCycle = (direction: "previous" | "next") => {
    // TODO: apply any animation?
    cycleRecord(direction);
  };

  const handleClose = () => {
    setClosed(true);
    setTimeout(() => closeInfo(), 500);
  };

  const setHoverImage = (url: string | null) => {
    if (!url) {
      dispatch(hoverImage(null));
      return;
    }
    // Get Image object; then dispatch action
    const image = record.IMAGES.find((img) => {
      const candidateUrls = [
        img.originalUrl,
        img.thumbnailUrls.small,
        img.thumbnailUrls.medium,
        img.thumbnailUrls.large
      ];
      return candidateUrls.some((imgUrl) => imgUrl === url);
    });
    if (!image) {
      dispatch(hoverImage(null));
    } else {
      dispatch(hoverImage(image.id));
    }
  };

  const panoramaViewer = (img: Image) => {
    return (
      <div onMouseOver={() => setHoverImage(img.originalUrl)}>
        <ThreeSixtyViewer src={img.originalUrl} />
      </div>
    );
  };

  const imageViewer = (img: Image) => {
    return (
      <img
        src={
          window.innerHeight == screen.height
            ? img.originalUrl
            : img.thumbnailUrls.medium
        }
        className="image-gallery-image"
        onMouseOver={() => setHoverImage(img.originalUrl)}
      />
    );
  };

  const thumbViewer = (img: Image) => {
    return (
      <div
        className="image-gallery-thumbnail-inner"
        onMouseOver={() => setHoverImage(img.originalUrl)}
      >
        <img
          src={img.thumbnailUrls.small}
          className="image-gallery-thumbnail-image"
        />
      </div>
    );
  };

  const galleryItems: GalleryItem[] = _.orderBy(
    record.IMAGES,
    ["isPrimary", "is360"],
    ["desc", "asc"]
  ).map((img) => {
    return img.is360
      ? {
          original: img.thumbnailUrls.medium,
          thumbnail: img.thumbnailUrls.small,
          renderItem: () => panoramaViewer(img),
          renderThumbInner: () => thumbViewer(img)
        }
      : {
          fullscreen: img.originalUrl,
          original: img.thumbnailUrls.medium,
          thumbnail: img.thumbnailUrls.small,
          renderItem: () => imageViewer(img),
          renderThumbInner: () => thumbViewer(img)
        };
  });

  return (
    <div
      id="info-panel"
      className={`${classes.container} ${isClosed ? "closed" : ""}`}
      onMouseEnter={() => !zoomedPeak && dispatch(hoverPeak(record.PEAK_ID))}
      onMouseLeave={() => dispatch(hoverPeak(null))}
    >
      <div className="info-nav-buttons">
        <div>
          <IconButton
            aria-label="previous"
            onClick={() => handleCycle("previous")}
            style={{ color: "white" }}
          >
            <ArrowBackIcon fontSize="large" />
          </IconButton>
          <IconButton
            aria-label="next"
            onClick={() => handleCycle("next")}
            style={{ color: "white" }}
          >
            <ArrowForwardIcon fontSize="large" />
          </IconButton>
        </div>
        <div></div>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          style={{ color: "white" }}
        >
          <ClearIcon fontSize="large" />
        </IconButton>
      </div>

      <div className="info-image" onMouseLeave={() => setHoverImage(null)}>
        {galleryItems.length ? (
          <ImageGallery
            items={galleryItems}
            showPlayButton={false}
            disableKeyDown={true}
          />
        ) : (
          <ImageGallery
            items={[{ original: placeholderImg }]}
            showPlayButton={false}
            showNav={false}
            showFullscreenButton={false}
            showThumbnails={false}
            disableKeyDown={true}
          />
        )}
      </div>

      <div className="info-data-overview info-data">
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="center"
        >
          <Typography variant="h6">
            <strong>{record.NAME}</strong>
          </Typography>
          <Typography variant="subtitle1">{record.ELEVATION + "'"}</Typography>
        </Grid>
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">
            <strong>{record.STATE}</strong>
          </Typography>
          <Typography variant="subtitle1">
            {record.SUMMIT_DATE?.toLocaleDateString() || "TBD"}
          </Typography>
        </Grid>
      </div>

      <hr />

      <Grid
        className="info-control-btns"
        container
        direction="row"
        justify="center"
        alignItems="center"
      >
        <IconButton
          aria-label="previous"
          onClick={() => dispatch(setZoomPeak(record.PEAK_ID))}
          style={{ color: "white" }}
        >
          <ThreeDRotation fontSize="large" />
        </IconButton>
      </Grid>

      <hr />

      <div className="info-data-details info-data">
        <Grid container spacing={3}>
          <DataLabel label="Latitude" value={record.LATITUDE} />
          <DataLabel label="Longitude" value={record.LONGITUDE} />
          {record.HIKING_TRAIL && (
            <DataLabel label="Hiking Trail" value={record.HIKING_TRAIL} />
          )}
          {record.HIKING_DISTANCE !== null && (
            <DataLabel
              label="Hiking Distance"
              value={record.HIKING_DISTANCE}
              suffix=" mi"
            />
          )}
          {record.HIKING_GAIN !== null && (
            <DataLabel
              label="Hiking Gain"
              value={record.HIKING_GAIN}
              suffix="'"
            />
          )}
          {record.HIKING_TIME !== null && (
            <DataLabel
              label="Hiking Time"
              value={new Date(record.HIKING_TIME * 1000)
                .toISOString()
                .substr(11, 5)}
            />
          )}

          <Grid item xs={12}>
            <Typography variant="overline">Permits + Restrictions</Typography>
            <Typography variant="body2" component="div">
              <ReactMarkdown linkTarget="_blank">
                {record.PERMITS_AND_RESTRICTIONS || "None"}
              </ReactMarkdown>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="overline">Special Equipment</Typography>
            <Typography variant="body2" component="div">
              <ReactMarkdown linkTarget="_blank">
                {record.SPECIAL_EQUIPMENT || "None"}
              </ReactMarkdown>
            </Typography>
          </Grid>
        </Grid>
      </div>

      <hr />

      <div className="info-blog-text info-data">
        <Typography variant="body2" component="div">
          <ReactMarkdown linkTarget="_blank">
            {record.BLOG_TEXT || ""}
          </ReactMarkdown>
        </Typography>
      </div>
    </div>
  );
}

export const MemoizedInfoPanel = React.memo(
  InfoPanel,
  (prevProps, nextProps) => {
    return prevProps.record == nextProps.record;
  }
);
